import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { forwardRef } from 'react';
import { cn } from '../../utils/cn';
import { useTheme } from '../../contexts/theme-context';
export const Radio = forwardRef(({ label, description, size = 'md', variant = 'default', showIcon = true, animateCheck = true, className, labelClassName, wrapperClassName, disabled, checked, ...props }, ref) => {
    const { theme } = useTheme();
    const isCompact = theme?.layoutDensity === 'compact';
    const isDark = theme?.mode === 'dark';
    // Size classes based on theme
    const sizeClasses = {
        sm: isCompact ? 'h-3 w-3' : 'h-3.5 w-3.5',
        md: isCompact ? 'h-4 w-4' : 'h-5 w-5',
        lg: isCompact ? 'h-5 w-5' : 'h-6 w-6',
    };
    // Icon size classes
    const iconSizeClasses = {
        sm: 'h-1.5 w-1.5',
        md: 'h-2 w-2',
        lg: 'h-2.5 w-2.5',
    };
    // Variant colors with theme-based primary support
    const variantClasses = {
        default: cn(
        // Use theme primary colors (from CSS variables)
        'border-gray-300 dark:border-gray-600', 'checked:bg-primary-600 dark:checked:bg-primary-500', 'checked:border-primary-600 dark:checked:border-primary-500', 'focus:ring-primary-500 dark:focus:ring-primary-400', 'hover:border-primary-400 dark:hover:border-primary-300', disabled && 'opacity-50 cursor-not-allowed'),
        primary: cn(
        // Explicitly use theme primary colors
        'border-primary-300 dark:border-primary-700', 'checked:bg-primary-600 dark:checked:bg-primary-500', 'checked:border-primary-600 dark:checked:border-primary-500', 'focus:ring-primary-500 dark:focus:ring-primary-400', 'hover:border-primary-400 dark:hover:border-primary-600', disabled && 'opacity-50 cursor-not-allowed'),
        success: cn('border-green-300 dark:border-green-700', 'checked:bg-green-600 dark:checked:bg-green-500', 'checked:border-green-600 dark:checked:border-green-500', 'focus:ring-green-500 dark:focus:ring-green-400', 'hover:border-green-400 dark:hover:border-green-600', disabled && 'opacity-50 cursor-not-allowed'),
        warning: cn('border-yellow-300 dark:border-yellow-700', 'checked:bg-yellow-600 dark:checked:bg-yellow-500', 'checked:border-yellow-600 dark:checked:border-yellow-500', 'focus:ring-yellow-500 dark:focus:ring-yellow-400', 'hover:border-yellow-400 dark:hover:border-yellow-600', disabled && 'opacity-50 cursor-not-allowed'),
        danger: cn('border-red-300 dark:border-red-700', 'checked:bg-red-600 dark:checked:bg-red-500', 'checked:border-red-600 dark:checked:border-red-500', 'focus:ring-red-500 dark:focus:ring-red-400', 'hover:border-red-400 dark:hover:border-red-600', disabled && 'opacity-50 cursor-not-allowed'),
    };
    const radioElement = (_jsxs("div", { className: "relative inline-flex items-center", children: [_jsx("input", { ref: ref, type: "radio", checked: checked, disabled: disabled, className: cn(
                // Base styles - circular for radio
                'appearance-none rounded-full border-2 cursor-pointer', 'transition-all duration-200 ease-in-out', 
                // Focus styles
                'focus:outline-none focus:ring-2 focus:ring-offset-2', isDark && 'focus:ring-offset-gray-900', !isDark && 'focus:ring-offset-white', 
                // Size
                sizeClasses[size], 
                // Variant colors
                variantClasses[variant], 
                // Shadow for depth
                !disabled && 'shadow-sm hover:shadow-md', 
                // Animation
                animateCheck && 'transform hover:scale-110', 
                // Custom className
                className), ...props }), showIcon && (_jsx("div", { className: cn('absolute inset-0 flex items-center justify-center pointer-events-none'), children: _jsx("div", { className: cn('rounded-full bg-white transition-all duration-200', iconSizeClasses[size], 'opacity-0 scale-0', checked && 'opacity-100 scale-100', animateCheck && 'transition-transform duration-200 ease-out') }) }))] }));
    // If no label, return just the radio
    if (!label && !description) {
        return radioElement;
    }
    // With label/description
    return (_jsxs("label", { className: cn('inline-flex items-start gap-2 cursor-pointer group', disabled && 'cursor-not-allowed opacity-50', wrapperClassName), children: [radioElement, _jsxs("div", { className: "flex flex-col", children: [label && (_jsx("span", { className: cn('select-none', isCompact ? 'text-sm' : 'text-base', 'text-gray-700 dark:text-gray-200', 'group-hover:text-gray-900 dark:group-hover:text-white', 'transition-colors duration-200', labelClassName), children: label })), description && (_jsx("span", { className: cn('select-none', isCompact ? 'text-xs' : 'text-sm', 'text-gray-500 dark:text-gray-400', 'mt-0.5'), children: description }))] })] }));
});
Radio.displayName = 'Radio';
export const RadioGroup = forwardRef(({ name, value, onChange, children, className, orientation = 'vertical', size, variant, disabled, ...props }, ref) => {
    const { theme } = useTheme();
    const isCompact = theme?.layoutDensity === 'compact';
    const handleChange = (event) => {
        if (onChange) {
            onChange(event.target.value);
        }
    };
    return (_jsx("div", { ref: ref, className: cn('flex', orientation === 'vertical' ? 'flex-col' : 'flex-row flex-wrap', orientation === 'vertical'
            ? (isCompact ? 'gap-2' : 'gap-3')
            : (isCompact ? 'gap-4' : 'gap-6'), className), role: "radiogroup", ...props, children: React.Children.map(children, (child) => {
            if (React.isValidElement(child) && child.type === Radio) {
                return React.cloneElement(child, {
                    name,
                    checked: child.props.value === value,
                    onChange: handleChange,
                    size: child.props.size || size,
                    variant: child.props.variant || variant,
                    disabled: child.props.disabled || disabled,
                });
            }
            return child;
        }) }));
});
RadioGroup.displayName = 'RadioGroup';
// Export a simple radio without label for use in tables
export const TableRadio = forwardRef((props, ref) => {
    return _jsx(Radio, { ref: ref, ...props, showIcon: false, animateCheck: false });
});
TableRadio.displayName = 'TableRadio';
